import React from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import { motion } from "framer-motion";
import { useTheme } from "@mui/material/styles";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import InsightsIcon from "@mui/icons-material/Insights";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";

const WhyChooseUs = () => {
  const theme = useTheme();

  const features = [
    {
      icon: <AccessTimeIcon sx={{ fontSize: 50 }} />,
      title: "100+ Hours Saved",
      description:
        "Save valuable time with automated bookkeeping and financial management",
      metric: "100+ hrs",
    },
    {
      icon: <AttachMoneyIcon sx={{ fontSize: 50 }} />,
      title: "$1000+ Cost Savings",
      description:
        "Reduce your accounting expenses significantly with our AI-powered solution",
      metric: "$1000+",
    },
    {
      icon: <InsightsIcon sx={{ fontSize: 50 }} />,
      title: "One-Click Real-Time Data Insights",
      description:
        "Get instant access to your financial data and insights with just one click",
      metric: "Real-time",
    },
    {
      icon: <AssessmentIcon sx={{ fontSize: 50 }} />,
      title: "100+ Industry-Leading Reports",
      description:
        "Access comprehensive reports and interactive dashboards tailored to your needs",
      metric: "100+ Reports",
    },
    {
      icon: <SupportAgentIcon sx={{ fontSize: 50 }} />,
      title: "24/7 Dedicated Support",
      description:
        "Round-the-clock expert support whenever you need assistance",
      metric: "24/7",
    },
  ];
  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        background: theme.palette.background.default,
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 2,
              }}
            >
              Why Choose f-ai?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.secondary,
                maxWidth: 700,
                mx: "auto",
              }}
            >
              Experience the future of financial management with cutting-edge AI
              technology
            </Typography>
          </Box>
        </motion.div>

        {/* Features Grid */}
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card
                  sx={{
                    height: "100%",
                    borderRadius: 3,
                    background: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-12px)",
                      boxShadow: theme.shadows[12],
                      borderColor: theme.palette.primary.main,
                      "& .icon-box": {
                        background: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                      },
                      "& .metric": {
                        color: theme.palette.primary.main,
                      },
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    {/* Icon */}
                    <Box
                      className="icon-box"
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: theme.palette.action.hover,
                        color: theme.palette.primary.main,
                        mb: 3,
                        transition: "all 0.3s ease",
                      }}
                    >
                      {feature.icon}
                    </Box>

                    {/* Metric */}
                    <Typography
                      className="metric"
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        mb: 1,
                        transition: "all 0.3s ease",
                      }}
                    >
                      {feature.metric}
                    </Typography>

                    {/* Title */}
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        mb: 2,
                      }}
                    >
                      {feature.title}
                    </Typography>

                    {/* Description */}
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        lineHeight: 1.7,
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default WhyChooseUs;
